import { ReactElement } from 'react';

// Types
import { GridConfig, HiddenRegion } from '../types';

// Utils
import { createIsDotHidden } from '../utils';

interface DotGridProps {
  grid: GridConfig;
  hiddenRegions: HiddenRegion[];
  width: number;
  height: number;
}

/**
 * Renders a grid of dots, skipping any dots inside hidden regions.
 *
 * @param props
 * @param props.grid - Grid configuration (rows, columns, padding, dot styling).
 * @param props.hiddenRegions - Areas where dots should not be rendered.
 * @param props.width - Pixel width of the drawing area.
 * @param props.height - Pixel height of the drawing area.
 */
export const DotGrid = ({
  grid,
  hiddenRegions,
  width,
  height,
}: DotGridProps): ReactElement => {
  const dots: ReactElement[] = [];
  // Build a lookup that tells whether a dot at (col,row) should be hidden.
  const isDotHidden = createIsDotHidden(hiddenRegions);

  // Walk the grid in row/column order and emit SVG circles.
  for (let row = 0; row < grid.rows; row++) {
    for (let col = 0; col < grid.cols; col++) {
      // Skip dots inside any hidden region.
      if (isDotHidden(col, row)) continue;

      // Convert grid indices into pixel positions within the padded area.
      // Evenly space dots across the inner area (size minus left/right or top/bottom padding):
      // x = paddingX + col * ((width - 2*paddingX) / (cols - 1)) and
      // y = paddingY + row * ((height - 2*paddingY) / (rows - 1)),
      // so the first/last dots sit on the padded bounds.
      const x =
        grid.paddingX + col * ((width - 2 * grid.paddingX) / (grid.cols - 1));
      const y =
        grid.paddingY + row * ((height - 2 * grid.paddingY) / (grid.rows - 1));

      // Draw a single dot centered at (x, y).
      dots.push(
        <circle
          key={`dot-${row}-${col}`}
          cx={x}
          cy={y}
          r={grid.dotSize / 2}
          fill={grid.dotColor}
        />,
      );
    }
  }

  return <>{dots}</>;
};
